// src/skills/glyphbinding/glyphbinding.js
// Glyphbinding skill - gather ichor and craft glyphs

import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import { addXP } from '../../systems/experience.js';
import { ESSENCE_NODES, GLYPH_RECIPES, GLYPH_CORE_RECIPES, FOCUS_RECIPES, CHISELS } from './glyphbindingData.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { getTalentLootBonus, rollTalentProc, getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

// All ichor types for Universal Glyph Catalyst substitution
const ALL_ICHOR_TYPES = ['arcane_residue', 'refined_arcane_ichor', 'purified_arcane_core'];

class GlyphbindingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.GLYPHBINDING, CHISELS, 'BASIC');
        this.onIchorComplete = null;
        this.onGlyphComplete = null;
        this.onGlyphCoreComplete = null;
        this.onFocusComplete = null;
    }

    /**
     * Get the currently equipped chisel (alias for BaseSkill method)
     */
    get currentChisel() {
        return this.getCurrentTool();
    }

    upgradeChisel(chiselId) {
        const chisel = Object.values(CHISELS).find(c => c.id === chiselId);
        if (!chisel) {
            return { success: false, reason: 'Chisel not found' };
        }

        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        if (skill.level < chisel.level) {
            return { success: false, reason: `Requires level ${chisel.level}` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.GLYPHBINDING, chiselId)) {
            gameState.equipTool(SKILLS.GLYPHBINDING, chiselId);
            return { success: true, alreadyOwned: true };
        }

        // Check gold
        if (gameState.getGold() < chisel.price) {
            return { success: false, reason: 'Not enough gold' };
        }

        // Purchase and equip
        const result = gameState.purchaseTool(SKILLS.GLYPHBINDING, chiselId, chisel.price);
        if (result.success) {
            gameState.equipTool(SKILLS.GLYPHBINDING, chiselId);
        }
        return result;
    }

    // ========== Ichor Gathering ==========

    canGatherIchor(nodeId) {
        const node = Object.values(ESSENCE_NODES).find(n => n.id === nodeId);
        if (!node) {
            return { allowed: false, reason: 'Node not found' };
        }

        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        if (skill.level < node.level) {
            return { allowed: false, reason: `Requires level ${node.level} Glyphbinding` };
        }

        // Check if output (ichor) can fit in inventory
        const outputItem = node.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { allowed: false, reason: 'Inventory full' };
        }

        return { allowed: true, node };
    }

    startGatheringIchor(nodeId, showToast = true) {
        const check = this.canGatherIchor(nodeId);
        if (!check.allowed) {
            if (showToast) toastWarning(check.reason);
            return false;
        }

        const node = check.node;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const duration = node.baseTime / speedMultiplier;

        // Capture reference to skill for use in tick function
        const skill = this;

        const action = {
            type: SKILLS.GLYPHBINDING,
            subType: 'ichor',
            name: `Gathering ${node.name}`,
            node: { id: node.id, name: node.name },
            duration,
            progress: 0,
            startTime: Date.now(),
            forceComplete: false,
            tick: function (gameSpeedMultiplier = 1) {
                // Handle force complete from admin panel
                if (this.forceComplete) {
                    this.forceComplete = false;
                    skill.completeGatheringIchor(node);
                    const canContinue = skill.canGatherIchor(nodeId);
                    if (canContinue.allowed) {
                        skill.startGatheringIchor(nodeId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - this.startTime) * gameSpeedMultiplier;
                this.progress = Math.min(elapsed / this.duration, 1);

                // Complete when progress reaches 1
                if (this.progress >= 1) {
                    skill.completeGatheringIchor(node);
                    const canContinue = skill.canGatherIchor(nodeId);
                    if (canContinue.allowed) {
                        skill.startGatheringIchor(nodeId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    stopGatheringIchor() {
        gameState.clearAction();
    }

    /**
     * Roll for glyphbinding talent procs (ichor gathering)
     */
    rollIchorProcs() {
        const procs = {
            bonusIchor: false,
            ichorBurst: false
        };

        // Check bonusIchor (flat bonus chance for extra ichor)
        const bonusIchorChance = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'bonusIchor');
        if (bonusIchorChance > 0 && Math.random() < bonusIchorChance) {
            procs.bonusIchor = true;
        }

        // Check ichorBurst (10% chance for 3x ichor)
        if (rollTalentProc(SKILLS.GLYPHBINDING, 'ichorBurst')) {
            procs.ichorBurst = true;
        }

        return procs;
    }

    completeGatheringIchor(node) {
        // addXP handles admin multiplier internally
        const xpResult = addXP(SKILLS.GLYPHBINDING, node.xp, node.id);

        // Roll for talent procs
        const procs = this.rollIchorProcs();

        // Pre-calculate random quantities for drops with min/max range
        const normalizedDrops = node.drops.map(drop => {
            let quantity = drop.quantity || 1;
            if (drop.minQuantity !== undefined && drop.maxQuantity !== undefined) {
                quantity = Math.floor(Math.random() * (drop.maxQuantity - drop.minQuantity + 1)) + drop.minQuantity;
            }
            return { itemId: drop.itemId, quantity };
        });

        // Apply talent loot bonus (convert from bonus to multiplier)
        const talentLootBonus = getTalentLootBonus(SKILLS.GLYPHBINDING);
        if (talentLootBonus > 0) {
            const lootMult = 1 + talentLootBonus;
            normalizedDrops.forEach(drop => {
                drop.quantity = Math.floor(drop.quantity * lootMult);
            });
        }

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.GLYPHBINDING, normalizedDrops);

        // Handle bonusIchor proc - award extra ichor
        if (procs.bonusIchor) {
            const bonusDrop = normalizedDrops[0]; // First drop is the ichor
            if (bonusDrop) {
                const bonusQty = Math.max(1, Math.floor(bonusDrop.quantity * 0.5)); // 50% bonus
                gameState.addItem(bonusDrop.itemId, bonusQty);
                recordTalentProc(SKILLS.GLYPHBINDING, 'Essence Siphon', null, null, { itemId: bonusDrop.itemId, quantity: bonusQty });
            }
        }

        // Handle ichorBurst proc - 3x ichor (2 extra drops since 1 already awarded)
        if (procs.ichorBurst) {
            const burstDrop = normalizedDrops[0]; // First drop is the ichor
            if (burstDrop) {
                // Award 2x more (for 3x total)
                awardDrops(SKILLS.GLYPHBINDING, [burstDrop]);
                awardDrops(SKILLS.GLYPHBINDING, [burstDrop]);
                recordTalentProc(SKILLS.GLYPHBINDING, 'Ichor Burst', null, null, { itemId: burstDrop.itemId, quantity: burstDrop.quantity * 3 });
            }
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.GLYPHBINDING);

        // Check for pilferer contract assistance (ichor = gathering)
        pilferingSkill.tryContractAssist('glyphbinding', 'ichor');

        if (this.onIchorComplete) {
            this.onIchorComplete(node, xpResult);
        }
    }

    getAvailableNodes() {
        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        return Object.values(ESSENCE_NODES).filter(n => skill.level >= n.level);
    }

    // ========== Glyph Crafting ==========

    canCraftGlyph(glyphId, ichorOverride = null) {
        const recipe = Object.values(GLYPH_RECIPES).find(r => r.id === glyphId);
        if (!recipe) {
            return { allowed: false, reason: 'Glyph not found' };
        }

        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        if (skill.level < recipe.level) {
            return { allowed: false, reason: `Requires level ${recipe.level} Glyphbinding` };
        }

        // Check for Universal Glyph Catalyst - allows any ichor type
        const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;
        let ichorToUse = ichorOverride || recipe.inputItem;
        let usingCatalyst = hasCatalyst; // Always use catalyst bonus if we have one

        // Check if we have enough of the selected ichor
        const hasSelectedIchor = gameState.getItemCount(ichorToUse) >= recipe.inputQuantity;

        if (!hasSelectedIchor) {
            if (hasCatalyst) {
                // Try to find any ichor that has enough quantity
                const substituteIchor = ALL_ICHOR_TYPES.find(ichor =>
                    gameState.getItemCount(ichor) >= recipe.inputQuantity
                );
                if (substituteIchor) {
                    ichorToUse = substituteIchor;
                } else {
                    return { allowed: false, reason: `Need ${recipe.inputQuantity} of any ichor (Catalyst active)` };
                }
            } else {
                return { allowed: false, reason: `Need ${recipe.inputQuantity} ${recipe.inputItem.replace(/_/g, ' ')}` };
            }
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { allowed: false, reason: 'Inventory full' };
        }

        return { allowed: true, recipe, ichorToUse, usingCatalyst };
    }

    startCraftingGlyph(glyphId, showToast = true, ichorOverride = null) {
        const check = this.canCraftGlyph(glyphId, ichorOverride);
        if (!check.allowed) {
            if (showToast) toastWarning(check.reason);
            return false;
        }

        const recipe = check.recipe;
        const ichorToUse = check.ichorToUse;
        const usingCatalyst = check.usingCatalyst;

        let speedMultiplier = this.getEffectiveSpeedMultiplier();

        // Apply Arcane Efficiency talent (glyph crafting speed bonus)
        const glyphCraftSpeedBonus = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'glyphCraftSpeed');
        if (glyphCraftSpeedBonus > 0) {
            speedMultiplier *= (1 + glyphCraftSpeedBonus);
        }

        const duration = recipe.baseTime / speedMultiplier;

        // Capture reference to skill for use in tick function
        const skill = this;

        const action = {
            type: SKILLS.GLYPHBINDING,
            subType: 'glyph',
            name: `Crafting ${recipe.name}${usingCatalyst ? ' (Catalyst)' : ''}`,
            glyph: { id: recipe.id, name: recipe.name },
            ichorToUse,
            usingCatalyst,
            duration,
            progress: 0,
            startTime: Date.now(),
            forceComplete: false,
            tick: function (gameSpeedMultiplier = 1) {
                // Handle force complete from admin panel
                if (this.forceComplete) {
                    this.forceComplete = false;
                    skill.completeCraftingGlyph(recipe);
                    // Check if we can continue (no toast on auto-restart) - pass selected ichor
                    const canContinue = skill.canCraftGlyph(glyphId, ichorToUse);
                    if (canContinue.allowed) {
                        skill.startCraftingGlyph(glyphId, false, ichorToUse);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - this.startTime) * gameSpeedMultiplier;
                this.progress = Math.min(elapsed / this.duration, 1);

                // Complete when progress reaches 1
                if (this.progress >= 1) {
                    skill.completeCraftingGlyph(recipe);
                    // Try to restart if we have materials (no toast on auto-restart) - pass selected ichor
                    const canContinue = skill.canCraftGlyph(recipe.id, ichorToUse);
                    if (canContinue.allowed) {
                        skill.startCraftingGlyph(recipe.id, false, ichorToUse);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    stopCraftingGlyph() {
        gameState.clearAction();
    }

    /**
     * Roll for glyph crafting talent procs
     * Priority: tripleGlyph (3x) > doubleGlyph (2x)
     */
    rollGlyphProcs() {
        const procs = {
            ichorSave: false,
            doubleGlyph: false,
            tripleGlyph: false,
            outputMultiplier: 1
        };

        // Roll ichorSave (material preservation - Efficient Bindings talent)
        if (rollTalentProc(SKILLS.GLYPHBINDING, 'ichorSave')) {
            procs.ichorSave = true;
        }

        // Roll output multipliers (mutually exclusive, highest wins)
        // Essence Overflow (5% for triple) > Resonant Glyphs (8% for double)
        if (rollTalentProc(SKILLS.GLYPHBINDING, 'tripleGlyph')) {
            procs.tripleGlyph = true;
            procs.outputMultiplier = 3;
        } else if (rollTalentProc(SKILLS.GLYPHBINDING, 'doubleGlyph')) {
            procs.doubleGlyph = true;
            procs.outputMultiplier = 2;
        }

        return procs;
    }

    completeCraftingGlyph(recipe, ichorToUse = null, usingCatalyst = false) {
        // Get output item ID for proc notifications
        const outputItemId = recipe.drops[0]?.itemId;

        // If no ichor specified, read from current action (for restart compatibility)
        const currentAction = gameState.getCurrentAction();
        if (!ichorToUse && currentAction?.ichorToUse) {
            ichorToUse = currentAction.ichorToUse;
            usingCatalyst = currentAction.usingCatalyst || false;
        }
        // Fallback to recipe default
        if (!ichorToUse) {
            ichorToUse = recipe.inputItem;
        }

        // Roll for talent procs first
        const procs = this.rollGlyphProcs();

        // Consume materials (unless ichorSave proc'd)
        // Note: Universal Glyph Catalyst is NOT consumed - it's a permanent upgrade item
        if (!procs.ichorSave) {
            const removed = gameState.removeItem(ichorToUse, recipe.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove ichor for glyph crafting');
                this.stopCraftingGlyph();
                return;
            }
        } else {
            recordTalentProc(SKILLS.GLYPHBINDING, 'Efficient Bindings', 'Ichor saved!', null);
        }

        // Award XP (addXP handles admin multiplier internally)
        const xpResult = addXP(SKILLS.GLYPHBINDING, recipe.xp, recipe.id);

        // Check for ashes bonus (auto-consume if available)
        // Base 50% chance, Ashes Affinity talent adds +25% (to 75%)
        let ashesBonus = false;
        if (gameState.getItemCount('ashes') >= 4) {
            gameState.removeItem('ashes', 4);
            // Base 50% + Ashes Affinity talent bonus
            const baseAshesChance = 0.50;
            const ashesAffinityBonus = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'ashesSuccessRate');
            const totalAshesChance = baseAshesChance + ashesAffinityBonus;
            if (Math.random() < totalAshesChance) {
                ashesBonus = true;
            }
        }

        // Calculate total output multiplier (talent proc multiplier + loot bonus)
        const talentLootBonus = getTalentLootBonus(SKILLS.GLYPHBINDING);
        const glyphOutputBonus = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'glyphOutputBonus');
        const baseDrops = recipe.drops.map(d => ({ ...d }));

        // Apply Universal Glyph Catalyst 2x output bonus (applied before other multipliers)
        if (usingCatalyst) {
            baseDrops.forEach(drop => {
                drop.quantity = (drop.quantity || 1) * 2;
            });
        }

        // Apply talent loot bonus and Sigil of Plenty bonus to base drops
        const totalLootMult = 1 + talentLootBonus + glyphOutputBonus;
        if (totalLootMult > 1) {
            baseDrops.forEach(drop => {
                drop.quantity = Math.floor((drop.quantity || 1) * totalLootMult);
            });
        }

        // Apply output proc multiplier
        if (procs.outputMultiplier > 1) {
            baseDrops.forEach(drop => {
                drop.quantity = (drop.quantity || 1) * procs.outputMultiplier;
            });
        }

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.GLYPHBINDING, baseDrops);

        // Record output multiplier procs
        if (procs.tripleGlyph && outputItemId) {
            recordTalentProc(SKILLS.GLYPHBINDING, 'Essence Overflow', null, null, { itemId: outputItemId, quantity: baseDrops[0]?.quantity || 3 });
        } else if (procs.doubleGlyph && outputItemId) {
            recordTalentProc(SKILLS.GLYPHBINDING, 'Resonant Glyphs', null, null, { itemId: outputItemId, quantity: baseDrops[0]?.quantity || 2 });
        }

        // Award bonus drop and XP if ashes proc'd
        if (ashesBonus && outputItemId) {
            awardDrops(SKILLS.GLYPHBINDING, recipe.drops);
            addXP(SKILLS.GLYPHBINDING, recipe.xp, recipe.id);
            recordTalentProc(SKILLS.GLYPHBINDING, 'Ashes Bonus', null, null, { itemId: outputItemId, quantity: 1 });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.GLYPHBINDING);

        // Check for pilferer contract assistance (glyph = processing)
        pilferingSkill.tryContractAssist('glyphbinding', 'glyph');

        if (this.onGlyphComplete) {
            this.onGlyphComplete(recipe, xpResult, ashesBonus);
        }
    }

    getAvailableGlyphs() {
        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        return Object.values(GLYPH_RECIPES).filter(r => skill.level >= r.level);
    }

    // ========== Glyph Core Crafting ==========

    canCraftGlyphCore(glyphCoreId, ichorOverride = null) {
        const recipe = Object.values(GLYPH_CORE_RECIPES).find(r => r.id === glyphCoreId);
        if (!recipe) {
            return { allowed: false, reason: 'Recipe not found' };
        }

        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        if (skill.level < recipe.level) {
            return { allowed: false, reason: `Requires level ${recipe.level} Glyphbinding` };
        }

        // Check for Universal Glyph Catalyst - allows any ichor type
        const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

        // Find ichor input in recipe (if any)
        const ichorInput = recipe.inputItems.find(input => ALL_ICHOR_TYPES.includes(input.itemId));
        let ichorToUse = ichorInput ? (ichorOverride || ichorInput.itemId) : null;

        // If catalyst active and ichor insufficient, find substitute
        if (hasCatalyst && ichorInput) {
            const ichorQty = gameState.getItemCount(ichorToUse);
            if (ichorQty < ichorInput.quantity) {
                const substituteIchor = ALL_ICHOR_TYPES.find(ichor =>
                    gameState.getItemCount(ichor) >= ichorInput.quantity
                );
                if (substituteIchor) {
                    ichorToUse = substituteIchor;
                }
            }
        }

        // Check all input materials (with ichor substitution)
        const missingMaterials = [];
        for (const input of recipe.inputItems) {
            const isIchor = ALL_ICHOR_TYPES.includes(input.itemId);
            const itemToCheck = (isIchor && hasCatalyst) ? ichorToUse : input.itemId;
            const playerQty = gameState.getItemCount(itemToCheck);
            if (playerQty < input.quantity) {
                const itemName = itemToCheck.replace(/_/g, ' ');
                missingMaterials.push(`${itemName}: ${playerQty}/${input.quantity}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { allowed: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { allowed: false, reason: 'Inventory full' };
        }

        return { allowed: true, recipe, ichorToUse };
    }

    startCraftingGlyphCore(glyphCoreId, showToast = true, ichorOverride = null) {
        const check = this.canCraftGlyphCore(glyphCoreId, ichorOverride);
        if (!check.allowed) {
            if (showToast) toastWarning(check.reason);
            return false;
        }

        const recipe = check.recipe;
        const ichorToUse = check.ichorToUse;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const duration = recipe.baseTime / speedMultiplier;

        const skill = this;

        const action = {
            type: SKILLS.GLYPHBINDING,
            subType: 'glyphCore',
            name: `Crafting ${recipe.name}`,
            glyphCore: { id: recipe.id, name: recipe.name },
            ichorToUse,
            duration,
            progress: 0,
            startTime: Date.now(),
            forceComplete: false,
            tick: function (gameSpeedMultiplier = 1) {
                if (this.forceComplete) {
                    this.forceComplete = false;
                    skill.completeCraftingGlyphCore(recipe, ichorToUse);
                    const canContinue = skill.canCraftGlyphCore(glyphCoreId, ichorToUse);
                    if (canContinue.allowed) {
                        skill.startCraftingGlyphCore(glyphCoreId, false, ichorToUse);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - this.startTime) * gameSpeedMultiplier;
                this.progress = Math.min(elapsed / this.duration, 1);

                if (this.progress >= 1) {
                    skill.completeCraftingGlyphCore(recipe, ichorToUse);
                    const canContinue = skill.canCraftGlyphCore(recipe.id, ichorToUse);
                    if (canContinue.allowed) {
                        skill.startCraftingGlyphCore(recipe.id, false, ichorToUse);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    stopCraftingGlyphCore() {
        gameState.clearAction();
    }

    completeCraftingGlyphCore(recipe, ichorToUse = null) {
        // Consume all input materials (use ichor override if provided)
        for (const input of recipe.inputItems) {
            const isIchor = ALL_ICHOR_TYPES.includes(input.itemId);
            const itemToConsume = (isIchor && ichorToUse) ? ichorToUse : input.itemId;
            const removed = gameState.removeItem(itemToConsume, input.quantity);
            if (!removed) {
                logger.warn(`Failed to remove ${itemToConsume} for glyph core crafting`);
                this.stopCraftingGlyphCore();
                return;
            }
        }

        // Award XP (addXP handles admin multiplier internally)
        const xpResult = addXP(SKILLS.GLYPHBINDING, recipe.xp, recipe.id);

        // Apply talent loot bonus to drops
        const talentLootBonus = getTalentLootBonus(SKILLS.GLYPHBINDING);
        const lootMult = talentLootBonus > 0 ? (1 + talentLootBonus) : 1;
        const modifiedDrops = recipe.drops.map(d => ({
            ...d,
            quantity: Math.floor((d.quantity || 1) * lootMult)
        }));

        // Award drops (no ashes bonus for glyph cores - only for glyphs)
        awardDrops(SKILLS.GLYPHBINDING, modifiedDrops);

        // Roll for corruption proc
        rollForCorruption(SKILLS.GLYPHBINDING);

        if (this.onGlyphCoreComplete) {
            this.onGlyphCoreComplete(recipe, xpResult);
        }
    }

    getAvailableGlyphCores() {
        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        return Object.values(GLYPH_CORE_RECIPES).filter(r => skill.level >= r.level);
    }

    // ========== Focus Crafting ==========

    canCraftFocus(focusId, ichorOverride = null) {
        const recipe = Object.values(FOCUS_RECIPES).find(r => r.id === focusId);
        if (!recipe) {
            return { allowed: false, reason: 'Recipe not found' };
        }

        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        if (skill.level < recipe.level) {
            return { allowed: false, reason: `Requires level ${recipe.level} Glyphbinding` };
        }

        // Check for Universal Glyph Catalyst - allows any ichor type
        const hasCatalyst = gameState.getItemCount('universal_glyph_catalyst') >= 1;

        // Find ichor input in recipe (if any)
        const ichorInput = recipe.inputItems.find(input => ALL_ICHOR_TYPES.includes(input.itemId));
        let ichorToUse = ichorInput ? (ichorOverride || ichorInput.itemId) : null;

        // If catalyst active and ichor insufficient, find substitute
        if (hasCatalyst && ichorInput) {
            const ichorQty = gameState.getItemCount(ichorToUse);
            if (ichorQty < ichorInput.quantity) {
                const substituteIchor = ALL_ICHOR_TYPES.find(ichor =>
                    gameState.getItemCount(ichor) >= ichorInput.quantity
                );
                if (substituteIchor) {
                    ichorToUse = substituteIchor;
                }
            }
        }

        // Check all input materials (with ichor substitution)
        const missingMaterials = [];
        for (const input of recipe.inputItems) {
            const isIchor = ALL_ICHOR_TYPES.includes(input.itemId);
            const itemToCheck = (isIchor && hasCatalyst) ? ichorToUse : input.itemId;
            const playerQty = gameState.getItemCount(itemToCheck);
            if (playerQty < input.quantity) {
                const itemName = itemToCheck.replace(/_/g, ' ');
                missingMaterials.push(`${itemName}: ${playerQty}/${input.quantity}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { allowed: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { allowed: false, reason: 'Inventory full' };
        }

        return { allowed: true, recipe, ichorToUse };
    }

    startCraftingFocus(focusId, showToast = true, ichorOverride = null) {
        const check = this.canCraftFocus(focusId, ichorOverride);
        if (!check.allowed) {
            if (showToast) toastWarning(check.reason);
            return false;
        }

        const recipe = check.recipe;
        const ichorToUse = check.ichorToUse;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const duration = recipe.baseTime / speedMultiplier;

        const skill = this;

        const action = {
            type: SKILLS.GLYPHBINDING,
            subType: 'focus',
            name: `Crafting ${recipe.name}`,
            focus: { id: recipe.id, name: recipe.name },
            ichorToUse,
            duration,
            progress: 0,
            startTime: Date.now(),
            forceComplete: false,
            tick: function (gameSpeedMultiplier = 1) {
                if (this.forceComplete) {
                    this.forceComplete = false;
                    skill.completeCraftingFocus(recipe, ichorToUse);
                    const canContinue = skill.canCraftFocus(focusId, ichorToUse);
                    if (canContinue.allowed) {
                        skill.startCraftingFocus(focusId, false, ichorToUse);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - this.startTime) * gameSpeedMultiplier;
                this.progress = Math.min(elapsed / this.duration, 1);

                if (this.progress >= 1) {
                    skill.completeCraftingFocus(recipe, ichorToUse);
                    const canContinue = skill.canCraftFocus(recipe.id, ichorToUse);
                    if (canContinue.allowed) {
                        skill.startCraftingFocus(recipe.id, false, ichorToUse);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    stopCraftingFocus() {
        gameState.clearAction();
    }

    completeCraftingFocus(recipe, ichorToUse = null) {
        // Consume all input materials (use ichor override if provided)
        for (const input of recipe.inputItems) {
            const isIchor = ALL_ICHOR_TYPES.includes(input.itemId);
            const itemToConsume = (isIchor && ichorToUse) ? ichorToUse : input.itemId;
            const removed = gameState.removeItem(itemToConsume, input.quantity);
            if (!removed) {
                logger.warn(`Failed to remove ${itemToConsume} for focus crafting`);
                this.stopCraftingFocus();
                return;
            }
        }

        // Award XP (addXP handles admin multiplier internally)
        const xpResult = addXP(SKILLS.GLYPHBINDING, recipe.xp, recipe.id);

        // Apply talent loot bonus to drops
        const talentLootBonus = getTalentLootBonus(SKILLS.GLYPHBINDING);
        const lootMult = talentLootBonus > 0 ? (1 + talentLootBonus) : 1;
        const modifiedDrops = recipe.drops.map(d => ({
            ...d,
            quantity: Math.floor((d.quantity || 1) * lootMult)
        }));

        // Award drops
        awardDrops(SKILLS.GLYPHBINDING, modifiedDrops);

        // Roll for corruption proc
        rollForCorruption(SKILLS.GLYPHBINDING);

        if (this.onFocusComplete) {
            this.onFocusComplete(recipe, xpResult);
        }
    }

    getAvailableFocuses() {
        const skill = gameState.getSkill(SKILLS.GLYPHBINDING);
        return Object.values(FOCUS_RECIPES).filter(r => skill.level >= r.level);
    }

    // ========== Action Completion Handler ==========

    handleActionComplete(action) {
        if (action.subType === 'ichor') {
            const node = Object.values(ESSENCE_NODES).find(n => n.id === action.node.id);
            if (node) {
                this.completeGatheringIchor(node);
                // Auto-restart
                this.startGatheringIchor(node.id);
            }
        } else if (action.subType === 'glyph') {
            const recipe = Object.values(GLYPH_RECIPES).find(r => r.id === action.glyph.id);
            if (recipe) {
                this.completeCraftingGlyph(recipe);
                // Check if we can continue
                const check = this.canCraftGlyph(recipe.id);
                if (check.allowed) {
                    this.startCraftingGlyph(recipe.id);
                } else {
                    gameState.clearAction();
                }
            }
        } else if (action.subType === 'glyphCore') {
            const recipe = Object.values(GLYPH_CORE_RECIPES).find(r => r.id === action.glyphCore.id);
            if (recipe) {
                this.completeCraftingGlyphCore(recipe);
                // Check if we can continue
                const check = this.canCraftGlyphCore(recipe.id);
                if (check.allowed) {
                    this.startCraftingGlyphCore(recipe.id);
                } else {
                    gameState.clearAction();
                }
            }
        } else if (action.subType === 'focus') {
            const recipe = Object.values(FOCUS_RECIPES).find(r => r.id === action.focus.id);
            if (recipe) {
                this.completeCraftingFocus(recipe);
                // Check if we can continue
                const check = this.canCraftFocus(recipe.id);
                if (check.allowed) {
                    this.startCraftingFocus(recipe.id);
                } else {
                    gameState.clearAction();
                }
            }
        }
    }
}

export const glyphbindingSkill = new GlyphbindingSkill();
